import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import WorkerCabinet from '@/components/workers/WorkerCabinet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { OrderPhotosGallery } from '@/components/PhotosFiles';
import { 
  Calendar, 
  Briefcase, 
  Clock, 
  CheckCircle2,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Play,
  Square,
  Phone,
  MessageSquare,
  MapPin,
  Navigation,
  Loader2,
  AlertTriangle,
  ChevronRight,
  Send,
  Wrench,
  Users,
  DollarSign,
} from 'lucide-react';
import { cn, localToday } from '@/lib/utils';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  new: { label: 'Новый', color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300' },
  confirmed: { label: 'Подтверждён', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  in_progress: { label: 'В работе', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  done: { label: 'Выполнен', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  cancelled: { label: 'Отменён', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
  deleted: { label: 'Удалён', color: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400' },
};

// Day Timer + Time Adjustment Request
function DayTimerSection({ dayStatus, orders, onDayAction, actionLoading, api, fetchData }) {
  const [dayElapsed, setDayElapsed] = useState(0);
  const [showTimeRequest, setShowTimeRequest] = useState(false);
  const [reqHours, setReqHours] = useState(0);
  const [reqMinutes, setReqMinutes] = useState(15);
  const [reqReason, setReqReason] = useState('');
  const [reqLoading, setReqLoading] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);

  const isHourly = dayStatus?.payment_type !== 'percentage';
  const allDone = orders.length > 0 && orders.every(o => o.operational_status === 'done' || o.operational_status === 'cancelled');
  const hasCompanyFaultService = orders.some(o => o.order_type === 'service' && o.fault_type === 'company_fault' && o.operational_status === 'done');

  // Day timer tick
  useEffect(() => {
    if (!dayStatus?.is_day_started || !dayStatus?.day_start_time) {
      setDayElapsed(0);
      return;
    }
    const startTime = new Date(dayStatus.day_start_time + (dayStatus.day_start_time.includes('Z') ? '' : 'Z'));
    const tick = () => {
      const endTime = dayStatus.day_end_time 
        ? new Date(dayStatus.day_end_time + (dayStatus.day_end_time.includes('Z') ? '' : 'Z'))
        : new Date();
      setDayElapsed(Math.max(0, Math.floor((endTime - startTime) / 1000)));
    };
    tick();
    if (!dayStatus.day_end_time) {
      const interval = setInterval(tick, 1000);
      return () => clearInterval(interval);
    }
  }, [dayStatus?.is_day_started, dayStatus?.day_start_time, dayStatus?.day_end_time]);

  // Check for pending time request
  useEffect(() => {
    api.get('/time-adjustments/my-pending').then(res => {
      setPendingRequest(res.data);
    }).catch(() => {});
  }, [api]);

  const formatDayTime = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const handleSubmitTimeRequest = async () => {
    const total = reqHours * 60 + reqMinutes;
    if (total <= 0) { toast.error('Выберите время'); return; }
    if (reqReason.trim().length < 10) { toast.warning('Опишите причину подробнее (мин. 10 символов)'); return; }
    setReqLoading(true);
    try {
      const today = localToday();
      await api.post('/time-adjustments', {
        date: today,
        requested_hours: reqHours,
        requested_minutes: reqMinutes,
        reason: reqReason.trim(),
      });
      toast.success('Запрос отправлен менеджеру');
      setShowTimeRequest(false);
      const res = await api.get('/time-adjustments/my-pending');
      setPendingRequest(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка отправки');
    } finally {
      setReqLoading(false);
    }
  };

  // For percentage workers: only show if company-fault service exists
  if (!isHourly && !hasCompanyFaultService) return null;

  return (
    <div className="space-y-2">
      {/* Day not started */}
      {isHourly && !dayStatus?.is_day_started && !dayStatus?.day_end_time && (
        <Button
          className="w-full h-14 text-base bg-green-600 hover:bg-green-700"
          onClick={() => onDayAction('start')}
          disabled={actionLoading}
          data-testid="start-day-btn"
        >
          {actionLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Play className="mr-2 h-5 w-5" />}
          Начать рабочий день
        </Button>
      )}

      {/* Day running — timer */}
      {dayStatus?.is_day_started && !dayStatus?.day_end_time && (
        <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium text-green-700 dark:text-green-400">
                Рабочий день
              </span>
            </div>
            <span className="text-xl font-mono font-bold tabular-nums text-green-600 dark:text-green-400" data-testid="day-timer">
              {formatDayTime(dayElapsed)}
            </span>
          </div>
          {allDone && (
            <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-800 flex items-center justify-between">
              <span className="text-xs text-green-600/70">Все заказы выполнены</span>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50 h-7 text-xs"
                onClick={() => onDayAction('end')}
                disabled={actionLoading}
                data-testid="end-day-btn"
              >
                <Square className="mr-1.5 h-3 w-3" /> Завершить день
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Day ended — show summary + time request */}
      {dayStatus?.day_end_time && (
        <div className="space-y-2">
          <div className="p-3 rounded-xl bg-muted/50 border text-center">
            <p className="text-sm text-muted-foreground">День завершён</p>
            <p className="text-lg font-mono font-bold tabular-nums mt-1" data-testid="day-total-time">
              {formatDayTime(dayElapsed)}
            </p>
          </div>

          {/* Pending request status */}
          {pendingRequest && pendingRequest.status === 'pending' && (
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
              <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Запрос на доп. время отправлен</p>
              <p className="text-xs text-amber-600/70 mt-1">
                +{pendingRequest.requested_hours}ч {pendingRequest.requested_minutes > 0 ? `${pendingRequest.requested_minutes}мин` : ''} — ожидает решения менеджера
              </p>
            </div>
          )}

          {pendingRequest && pendingRequest.status === 'approved' && (
            <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
              <p className="text-sm font-medium text-green-700 dark:text-green-400">Доп. время одобрено</p>
              <p className="text-xs text-green-600/70 mt-1">
                +{pendingRequest.approved_hours}ч {pendingRequest.approved_minutes > 0 ? `${pendingRequest.approved_minutes}мин` : ''}
                {pendingRequest.manager_comment && ` — ${pendingRequest.manager_comment}`}
              </p>
            </div>
          )}

          {pendingRequest && pendingRequest.status === 'rejected' && (
            <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800">
              <p className="text-sm font-medium text-red-700 dark:text-red-400">Запрос отклонён</p>
              {pendingRequest.manager_comment && (
                <p className="text-xs text-red-600/70 mt-1">{pendingRequest.manager_comment}</p>
              )}
            </div>
          )}

          {/* Request additional time */}
          {(!pendingRequest || pendingRequest.status === 'rejected') && (isHourly || hasCompanyFaultService) && (
            <>
              {!showTimeRequest ? (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setShowTimeRequest(true)}
                  data-testid="request-time-btn"
                >
                  <Clock className="mr-2 h-4 w-4" />
                  Запросить доп. время
                </Button>
              ) : (
                <Card className="cgc-card">
                  <CardContent className="p-4 space-y-3">
                    <h3 className="text-sm font-semibold">Запрос доп. времени</h3>
                    
                    {/* Hour/Minute picker */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground">Часы</label>
                        <div className="relative mt-1">
                          <select
                            value={reqHours}
                            onChange={e => setReqHours(parseInt(e.target.value))}
                            className="w-full h-12 text-center text-xl font-mono font-bold rounded-lg border bg-background appearance-none cursor-pointer"
                            data-testid="time-hours-picker"
                          >
                            {[0,1,2,3,4,5,6,7,8,9,10].map(h => (
                              <option key={h} value={h}>{h}</option>
                            ))}
                          </select>
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">ч</span>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Минуты</label>
                        <div className="relative mt-1">
                          <select
                            value={reqMinutes}
                            onChange={e => setReqMinutes(parseInt(e.target.value))}
                            className="w-full h-12 text-center text-xl font-mono font-bold rounded-lg border bg-background appearance-none cursor-pointer"
                            data-testid="time-minutes-picker"
                          >
                            {[0,15,30,45].map(m => (
                              <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                            ))}
                          </select>
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">мин</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground text-center">
                      Запрос: <span className="font-semibold">{reqHours > 0 ? `${reqHours} ч ` : ''}{reqMinutes > 0 ? `${reqMinutes} мин` : reqHours > 0 ? '' : '0 мин'}</span>
                    </p>

                    {/* Reason */}
                    <textarea
                      value={reqReason}
                      onChange={e => setReqReason(e.target.value)}
                      placeholder="Добавьте мне пожалуйста X часов потому что..."
                      className="w-full min-h-[60px] p-2 text-sm rounded-lg border bg-background resize-none"
                      data-testid="time-reason"
                    />

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setShowTimeRequest(false)}
                      >
                        Отмена
                      </Button>
                      <Button
                        className="flex-1"
                        onClick={handleSubmitTimeRequest}
                        disabled={reqLoading}
                        data-testid="submit-time-request"
                      >
                        {reqLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        Отправить
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Order Card for Worker - optimized for 1-2 tap actions
function WorkerOrderCard({ order, onOpen, isActive }) {
  return (
    <button
      onClick={() => onOpen(order)}
      className={cn(
        "w-full text-left p-4 rounded-xl border transition-all hover:shadow-md",
        isActive && "border-amber-400 bg-amber-50 dark:bg-amber-900/10 ring-2 ring-amber-400/30",
        !isActive && "border-border hover:border-accent/30"
      )}
      data-testid={`worker-order-${order.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: order.service_color + '20' }}
          >
            <Briefcase className="h-5 w-5" style={{ color: order.service_color }} />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-sm truncate">{order.title}</p>
            <p className="text-xs text-muted-foreground">{order.client_name}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {order.scheduled_time_start && (
            <Badge variant="outline" className="text-xs font-mono">
              {order.scheduled_time_start}
            </Badge>
          )}
          <Badge className={cn("text-[10px]", STATUS_CONFIG[order.operational_status]?.color)}>
            {STATUS_CONFIG[order.operational_status]?.label}
          </Badge>
        </div>
      </div>

      {/* Quick info row */}
      <div className="flex items-center gap-3 mt-3 text-xs text-muted-foreground">
        {order.client_address && (
          <span className="flex items-center gap-1 truncate">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{order.client_address}</span>
          </span>
        )}
        {order.estimated_duration_minutes && (
          <span className="flex items-center gap-1 shrink-0">
            <Clock className="h-3 w-3" /> {order.estimated_duration_minutes} мин
          </span>
        )}
      </div>

      {order.service_required && (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>Требуется доп. обслуживание</span>
        </div>
      )}
    </button>
  );
}

// Order Detail Sheet for Worker - quick actions in 1-2 taps
function OrderDetailSheet({ order, open, onOpenChange, onAction, actionLoading, allOrders }) {
  const [note, setNote] = useState('');
  const [countdown, setCountdown] = useState(null); // seconds remaining
  const [elapsed, setElapsed] = useState(0); // seconds elapsed
  const [geoError, setGeoError] = useState('');
  const [geoChecking, setGeoChecking] = useState(false);
  const { t } = useLanguage();

  const handleStartWork = () => {
    if (!navigator.geolocation) {
      setGeoError('Геолокация не поддерживается вашим устройством. Обратитесь к менеджеру.');
      return;
    }
    setGeoError('');
    setGeoChecking(true);
    navigator.geolocation.getCurrentPosition(
      () => {
        // Permission granted — proceed
        setGeoChecking(false);
        onAction('start-work', order.id);
      },
      () => {
        // Permission denied or error
        setGeoChecking(false);
        setGeoError('Разрешите доступ к геолокации чтобы начать работу. Откройте настройки браузера и разрешите местоположение для этого сайта.');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  
  // Start/update countdown timer when order is in_progress
  useEffect(() => {
    if (!order || order.operational_status !== 'in_progress') {
      setCountdown(null);
      setElapsed(0);
      return;
    }
    
    const estimatedSec = (order.estimated_duration_minutes || 0) * 60;
    const startedAt = order.started_at ? new Date(order.started_at) : new Date();
    
    const tick = () => {
      const now = new Date();
      const elapsedSec = Math.floor((now - startedAt) / 1000);
      const remaining = estimatedSec - elapsedSec;
      setElapsed(elapsedSec);
      setCountdown(remaining);
    };
    
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [order?.id, order?.operational_status, order?.started_at, order?.estimated_duration_minutes]);

  if (!order) return null;

  const formatTime = (totalSeconds) => {
    const abs = Math.abs(totalSeconds);
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    const s = abs % 60;
    const sign = totalSeconds < 0 ? '-' : '';
    if (h > 0) return `${sign}${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${sign}${m}:${String(s).padStart(2, '0')}`;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '?';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h > 0 && m > 0) return `${h} ч ${m} мин`;
    if (h > 0) return `${h} ч`;
    return `${m} мин`;
  };

  const handleCall = () => {
    if (order.client_phone) window.location.href = `tel:${order.client_phone}`;
  };
  const handleSms = () => {
    if (order.client_phone) window.location.href = `sms:${order.client_phone}`;
  };
  const handleRoute = () => {
    const addr = encodeURIComponent(order.client_address || order.title);
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${addr}`, '_blank');
  };

  const isOvertime = countdown !== null && countdown < 0;
  const isAlmostDone = countdown !== null && countdown >= 0 && countdown < 300; // last 5 min

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] rounded-t-2xl">
        <SheetHeader className="text-left">
          <SheetTitle className="truncate text-lg">{order.title}</SheetTitle>
          <SheetDescription asChild>
            <div className="flex items-center gap-2">
              <Badge className={cn("text-xs", STATUS_CONFIG[order.operational_status]?.color)}>
                {STATUS_CONFIG[order.operational_status]?.label}
              </Badge>
              {order.service_name && <span className="text-xs">{order.service_name}</span>}
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4 overflow-y-auto pb-safe" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          {/* Quick Action Buttons - CTA 1-2 taps */}
          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              className="h-16 flex-col gap-1"
              onClick={handleCall}
              disabled={!order.client_phone}
              data-testid="action-call"
            >
              <Phone className="h-5 w-5 text-green-500" />
              <span className="text-xs">{t('worker.call')}</span>
            </Button>
            <Button
              variant="outline"
              className="h-16 flex-col gap-1"
              onClick={handleSms}
              disabled={!order.client_phone}
              data-testid="action-sms"
            >
              <MessageSquare className="h-5 w-5 text-blue-500" />
              <span className="text-xs">{t('worker.sms')}</span>
            </Button>
            <Button
              variant="outline"
              className="h-16 flex-col gap-1"
              onClick={handleRoute}
              data-testid="action-route"
            >
              <Navigation className="h-5 w-5 text-accent" />
              <span className="text-xs">{t('worker.route')}</span>
            </Button>
          </div>

          {/* Client Info */}
          <Card className="border">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm">{order.client_address || t('worker.addressNotSet')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm">
                  {order.scheduled_time_start || '?'} — {order.scheduled_time_end || '?'} ({formatDuration(order.estimated_duration_minutes)})
                </span>
              </div>
              {order.description && (
                <p className="text-sm text-muted-foreground pt-1 border-t">{order.description}</p>
              )}
            </CardContent>
          </Card>

          {/* Primary Action Button / Timer */}
          <div className="space-y-2">
            {(order.operational_status === 'new' || order.operational_status === 'confirmed') && (
              <>
                <Button
                  className="w-full h-14 text-base bg-green-600 hover:bg-green-700"
                  onClick={handleStartWork}
                  disabled={actionLoading || geoChecking}
                  data-testid="action-start-work"
                >
                  {(actionLoading || geoChecking) ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <Play className="mr-2 h-5 w-5" />}
                  {geoChecking ? 'Проверка геолокации...' : t('worker.startWork')}
                </Button>
                {geoError && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{geoError}</span>
                  </div>
                )}
              </>
            )}
            {order.operational_status === 'in_progress' && (
              <>
                {/* Countdown Timer */}
                <div className={cn(
                  "p-4 rounded-xl border-2 text-center transition-colors",
                  isOvertime ? "border-red-500/50 bg-red-500/10" :
                  isAlmostDone ? "border-amber-500/50 bg-amber-500/10" :
                  "border-green-500/50 bg-green-500/10"
                )} data-testid="work-timer">
                  <p className="text-xs text-muted-foreground mb-1">
                    {isOvertime ? 'Превышение времени' : 'Осталось'}
                  </p>
                  <p className={cn(
                    "text-4xl font-mono font-bold tabular-nums",
                    isOvertime ? "text-red-600 dark:text-red-400" :
                    isAlmostDone ? "text-amber-600 dark:text-amber-400" :
                    "text-green-600 dark:text-green-400"
                  )}>
                    {countdown !== null ? formatTime(countdown) : '--:--'}
                  </p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    из {formatDuration(order.estimated_duration_minutes)}
                  </p>
                  <div className="w-full h-1.5 rounded-full bg-muted mt-3 overflow-hidden">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all duration-1000",
                        isOvertime ? "bg-red-500" : isAlmostDone ? "bg-amber-500" : "bg-green-500"
                      )}
                      style={{ width: `${Math.min(100, (elapsed / ((order.estimated_duration_minutes || 1) * 60)) * 100)}%` }}
                    />
                  </div>
                </div>
                <Button
                  className={cn(
                    "w-full h-14 text-base",
                    isOvertime ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"
                  )}
                  onClick={() => onAction('finish-work', order.id)}
                  disabled={actionLoading}
                  data-testid="action-finish-work"
                >
                  {actionLoading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CheckCircle2 className="mr-2 h-5 w-5" />}
                  {t('worker.finishWork')}
                </Button>
              </>
            )}
            {order.operational_status !== 'done' && order.operational_status !== 'cancelled' && (
              <Button
                variant="outline"
                className="w-full h-12 text-amber-600 border-amber-300 hover:bg-amber-50"
                onClick={() => onAction('mark-requires-service', order.id)}
                disabled={actionLoading || order.service_required}
                data-testid="action-requires-service"
              >
                <Wrench className="mr-2 h-4 w-4" />
                {order.service_required ? t('worker.alreadyMarked') : t('worker.requiresService')}
              </Button>
            )}
          </div>

          {/* Add Note */}
          {order.operational_status !== 'done' && order.operational_status !== 'cancelled' && (
            <div className="space-y-2">
              <p className="text-sm font-medium">{t('worker.addNote')}</p>
              <div className="flex gap-2">
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={t('worker.addNotePlaceholder')}
                  className="min-h-[44px] resize-none"
                  rows={2}
                  data-testid="order-note-input"
                />
                <Button
                  size="icon"
                  className="shrink-0 h-[68px] w-12"
                  disabled={!note.trim() || actionLoading}
                  onClick={() => {
                    onAction('add-note', order.id, note.trim());
                    setNote('');
                  }}
                  data-testid="order-note-send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Photos */}
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('photos.title')}</p>
            <OrderPhotosGallery orderId={order.id} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Haversine distance in meters between two GPS points
function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Worker Dashboard - "Мой день"
function WorkerDashboard({ user }) {
  const { api } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [dayStatus, setDayStatus] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const today = new Date().toLocaleDateString(lang === 'en' ? 'en-US' : 'ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });

  // Geo-tracker: alert managers/admins when worker leaves job site >300m
  const alertedOrderIds = useRef(new Set());
  useEffect(() => {
    if (!navigator.geolocation) return;

    const trackableOrders = orders.filter(o =>
      o.operational_status === 'in_progress' && o.service_lat && o.service_lng
    );
    if (trackableOrders.length === 0) return;

    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        for (const order of trackableOrders) {
          if (alertedOrderIds.current.has(order.id)) continue;
          const dist = haversineMeters(latitude, longitude, order.service_lat, order.service_lng);
          if (dist > 300) {
            alertedOrderIds.current.add(order.id);
            const isLastOrder = orders.filter(
              o => !['done', 'cancelled'].includes(o.operational_status)
            ).length === 1;
            api.post(`/orders/${order.id}/location-alert`, {
              worker_lat: latitude,
              worker_lng: longitude,
              is_last_order: isLastOrder,
            }).catch(() => {});
          }
        }
      },
      () => {}, // silently ignore geolocation errors
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 15000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [orders, api]);

  const fetchData = useCallback(async () => {
    try {
      const [statusRes, ordersRes] = await Promise.all([
        api.get('/worker/me/day-status'),
        api.get('/worker/me/orders'),
      ]);
      setDayStatus(statusRes.data);
      setOrders(ordersRes.data);
    } catch (err) {
      console.error('Failed to fetch worker data:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDayAction = async (action) => {
    setActionLoading(true);
    try {
      await api.post(`/worker/me/day/${action}`);
      toast.success(action === 'start' ? t('worker.dayStartedMsg') : t('worker.dayEndedMsg'));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleOrderAction = async (action, orderId, noteText) => {
    setActionLoading(true);
    try {
      if (action === 'add-note') {
        await api.post(`/orders/${orderId}/notes`, { text: noteText });
        toast.success(t('worker.noteAdded'));
      } else {
        const res = await api.post(`/orders/${orderId}/actions/${action}`);
        
        if (action === 'start-work') {
          toast.success('Работа начата! Таймер запущен');
        } else if (action === 'finish-work') {
          const data = res.data;
          const diff = data.time_diff_minutes || 0;
          const isLast = data.is_last_order;
          
          if (diff > 0) {
            // Finished early
            toast.success(`Отличная работа! Вы справились на ${diff} мин быстрее!`, {
              duration: 5000,
            });
          } else if (diff < 0 && !isLast) {
            // Finished late, more orders to go
            toast.warning('Поторопитесь, пожалуйста! Следующий клиент ждёт вас', {
              duration: 5000,
            });
          } else {
            // Last order or on time
            toast.success(t('worker.workFinished'));
          }
        } else {
          toast.success(t('worker.marked'));
        }
      }
      fetchData();
      if (action === 'finish-work') setDetailOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  const activeOrders = orders.filter(o => o.operational_status === 'in_progress');
  const pendingOrders = orders.filter(o => ['new', 'confirmed'].includes(o.operational_status));
  const doneOrders = orders.filter(o => o.operational_status === 'done');

  return (
    <div className="space-y-5 max-w-2xl mx-auto pb-20">
      {/* Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-foreground capitalize">{today}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t('worker.hello', user.first_name)} {dayStatus?.is_day_started ? t('worker.haveAGoodDay') : t('worker.readyToStart')}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="cgc-card">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold tabular-nums text-accent">{dayStatus?.total_orders_today || 0}</p>
            <p className="text-[11px] text-muted-foreground">{t('worker.ordersCount')}</p>
          </CardContent>
        </Card>
        <Card className="cgc-card">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold tabular-nums text-amber-500">{dayStatus?.in_progress_orders_today || 0}</p>
            <p className="text-[11px] text-muted-foreground">{t('worker.inProgress')}</p>
          </CardContent>
        </Card>
        <Card className="cgc-card">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold tabular-nums text-green-500">{dayStatus?.completed_orders_today || 0}</p>
            <p className="text-[11px] text-muted-foreground">{t('worker.completed')}</p>
          </CardContent>
        </Card>
      </div>

      {/* Day Timer Section */}
      <DayTimerSection
        dayStatus={dayStatus}
        orders={orders}
        onDayAction={handleDayAction}
        actionLoading={actionLoading}
        api={api}
        fetchData={fetchData}
      />

      {/* Active Work */}
      {activeOrders.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
            <Play className="h-4 w-4" /> {t('worker.currentlyActive')}
          </h2>
          {activeOrders.map(order => (
            <WorkerOrderCard
              key={order.id}
              order={order}
              onOpen={(o) => { setSelectedOrder(o); setDetailOpen(true); }}
              isActive
            />
          ))}
        </div>
      )}

      {/* Pending Orders */}
      {pendingOrders.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold flex items-center gap-1.5">
            <Calendar className="h-4 w-4" /> {t('worker.upcoming', pendingOrders.length)}
          </h2>
          {pendingOrders.map(order => (
            <WorkerOrderCard
              key={order.id}
              order={order}
              onOpen={(o) => { setSelectedOrder(o); setDetailOpen(true); }}
            />
          ))}
        </div>
      )}

      {/* Completed */}
      {doneOrders.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-green-600 dark:text-green-400 flex items-center gap-1.5">
            <CheckCircle2 className="h-4 w-4" /> {t('worker.completedSection', doneOrders.length)}
          </h2>
          {doneOrders.map(order => (
            <WorkerOrderCard
              key={order.id}
              order={order}
              onOpen={(o) => { setSelectedOrder(o); setDetailOpen(true); }}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {orders.length === 0 && (
        <Card className="cgc-card">
          <CardContent className="p-8 text-center">
            <Briefcase className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">{t('worker.noOrdersToday')}</p>
            <p className="text-sm text-muted-foreground mt-1">{t('worker.ordersWillAppear')}</p>
          </CardContent>
        </Card>
      )}

      {/* Order Detail Sheet */}
      <OrderDetailSheet
        order={selectedOrder}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onAction={handleOrderAction}
        actionLoading={actionLoading}
        allOrders={orders}
      />
    </div>
  );
}

// Manager/Admin Dashboard
function ManagerDashboard({ user }) {
  const { api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, done: 0, in_progress: 0, workers: 0, unpaid_done: 0, unpaid_done_amount: 0, remote_count: 0, remote_amount: 0 });
  const [serviceStats, setServiceStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schedulingAlerts, setSchedulingAlerts] = useState(null);
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [deleteDialogOrder, setDeleteDialogOrder] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const today = localToday();
        const [ordersRes, alertsRes, unpaidRes, partialRes, inProgressRes, remoteRes, serviceRes, cancelledRes] = await Promise.all([
          api.get(`/orders?date_from=${today}&date_to=${today}`),
          api.get('/dashboard/alerts/scheduling').catch(() => ({ data: null })),
          api.get('/orders?op_status=done&pay_status=unpaid&limit=200').catch(() => ({ data: [] })),
          api.get('/orders?op_status=done&pay_status=partial&limit=200').catch(() => ({ data: [] })),
          api.get('/orders?op_status=in_progress&limit=200').catch(() => ({ data: [] })),
          api.get('/orders/remote-stats').catch(() => ({ data: { unassigned_remote: 0 } })),
          api.get('/orders/service-stats').catch(() => ({ data: null })),
          api.get('/orders?op_status=cancelled&limit=50').catch(() => ({ data: [] })),
        ]);
        const orders = ordersRes.data;
        const unpaidOrders = [...(unpaidRes.data || []), ...(partialRes.data || [])];
        const unpaidAmount = unpaidOrders.reduce((sum, o) => {
          const remaining = (o.total_amount || 0) - (o.amount_paid || 0);
          return sum + Math.max(0, remaining);
        }, 0);
        setStats({
          total: orders.filter(o => !['cancelled', 'deleted'].includes(o.operational_status)).length,
          done: orders.filter(o => o.operational_status === 'done').length,
          in_progress: (inProgressRes.data || []).length,
          unpaid_done: unpaidOrders.length,
          unpaid_done_amount: Math.round(unpaidAmount * 100) / 100,
          remote_count: remoteRes.data?.unassigned_remote || 0,
          remote_amount: remoteRes.data?.total_amount || 0,
        });
        setCancelledOrders(cancelledRes.data || []);
        if (serviceRes.data) setServiceStats(serviceRes.data);
        if (alertsRes.data) setSchedulingAlerts(alertsRes.data);
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [api]);

  const handleReopen = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/actions/change-operational-status`, { new_status: 'new' });
      setCancelledOrders(prev => prev.filter(o => o.id !== orderId));
      toast.success('Заказ возвращён в работу');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDeleteOrder = async () => {
    if (!deleteDialogOrder || !deleteReason.trim()) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/orders/${deleteDialogOrder.id}`, { data: { reason: deleteReason.trim() } });
      setCancelledOrders(prev => prev.filter(o => o.id !== deleteDialogOrder.id));
      setDeleteDialogOrder(null);
      setDeleteReason('');
      toast.success('Заказ удалён');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ошибка удаления');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground">
          {t('manager.welcome', user.first_name)}
        </h1>
        <p className="text-muted-foreground mt-1">{t('manager.businessOverview')}</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Заказы сегодня */}
        <Card className="cgc-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/calendar')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-accent" />
              </div>
              <span className="text-xs text-muted-foreground">сегодня</span>
            </div>
            <p className="text-2xl font-bold tabular-nums">{loading ? '...' : stats.total}</p>
            <p className="text-sm text-muted-foreground">Заказов сегодня</p>
          </CardContent>
        </Card>

        {/* В процессе — все активные */}
        <Card className={`cgc-card cursor-pointer hover:shadow-md transition-shadow ${stats.in_progress > 0 ? 'border-amber-500/40' : ''}`} onClick={() => navigate('/orders?filter=in_progress')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Play className="h-5 w-5 text-amber-500" />
              </div>
              {stats.in_progress > 0 && <span className="text-xs font-medium text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded-full">активно</span>}
            </div>
            <p className="text-2xl font-bold tabular-nums">{loading ? '...' : stats.in_progress}</p>
            <p className="text-sm text-muted-foreground">В процессе</p>
          </CardContent>
        </Card>

        {/* Выполнено сегодня */}
        <Card className="cgc-card cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/orders?filter=done_today')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>
              <span className="text-xs text-muted-foreground">{stats.total ? `${Math.round(stats.done / stats.total * 100)}%` : '—'}</span>
            </div>
            <p className="text-2xl font-bold tabular-nums">{loading ? '...' : stats.done}</p>
            <p className="text-sm text-muted-foreground">Выполнено сегодня</p>
          </CardContent>
        </Card>

        {/* Требуют оплаты — ключевой виджет */}
        <Card className={`cgc-card cursor-pointer hover:shadow-md transition-shadow ${stats.unpaid_done > 0 ? 'border-red-500/40 bg-red-500/5' : ''}`} onClick={() => navigate('/orders?filter=needs_payment')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-red-500" />
              </div>
              {stats.unpaid_done > 0 && <span className="text-xs font-medium text-red-600 bg-red-500/10 px-2 py-0.5 rounded-full animate-pulse">!</span>}
            </div>
            <p className={`text-2xl font-bold tabular-nums ${stats.unpaid_done > 0 ? 'text-red-600' : ''}`}>{loading ? '...' : stats.unpaid_done > 0 ? `$${stats.unpaid_done_amount.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}</p>
            <p className="text-sm text-muted-foreground">{stats.unpaid_done > 0 ? `${stats.unpaid_done} без оплаты` : 'Всё оплачено'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Scheduling Alerts (if any) */}
      {schedulingAlerts && (schedulingAlerts.alerts?.slot_approved_no_order > 0 || schedulingAlerts.alerts?.pending_drafts > 0) && (
        <Card className="cgc-card-interactive border-amber-500/30" data-testid="scheduling-alerts-card">
          <CardContent className="p-4 space-y-2">
            {schedulingAlerts.alerts?.slot_approved_no_order > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div>
                  <p className="text-sm font-medium">{schedulingAlerts.alerts.slot_approved_no_order} slot(s) locked, order not created</p>
                  <p className="text-xs text-muted-foreground">Complete these orders before the lock expires</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate('/scheduling')} data-testid="alert-go-scheduling">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            {schedulingAlerts.alerts?.pending_drafts > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-500/10 border border-slate-500/20">
                <div>
                  <p className="text-sm font-medium">{schedulingAlerts.alerts.pending_drafts} draft(s) without selected slot</p>
                  <p className="text-xs text-muted-foreground">Select a slot or discard these drafts</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => navigate('/scheduling')} data-testid="alert-go-drafts">
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cancelled Orders Section */}
      {cancelledOrders.length > 0 && (
        <Card className="cgc-card border-red-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              Отменённые заказы
              <Badge variant="destructive" className="ml-auto">{cancelledOrders.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-4 space-y-2">
            {cancelledOrders.map(order => (
              <div key={order.id} className="flex items-center gap-3 p-3 rounded-lg border bg-red-50/50 dark:bg-red-900/10">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{order.client?.name || 'Клиент'}</p>
                  <p className="text-xs text-muted-foreground">
                    {order.title}
                    {order.scheduled_date && ` — ${new Date(order.scheduled_date).toLocaleDateString('ru-RU')}`}
                    {order.scheduled_time_start && ` ${order.scheduled_time_start}`}
                  </p>
                  {order.cancelled_by && (
                    <p className="text-xs text-red-500 mt-0.5">Отменил: {order.cancelled_by}</p>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleReopen(order.id)}>
                    Вернуть
                  </Button>
                  <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={() => { setDeleteDialogOrder(order); setDeleteReason(''); }}>
                    Удалить
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Delete Order Dialog */}
      {deleteDialogOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDeleteDialogOrder(null)}>
          <div className="bg-background rounded-lg shadow-xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Удалить заказ</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {deleteDialogOrder.title} — {deleteDialogOrder.client?.name}
            </p>
            <label className="text-sm font-medium">Причина удаления *</label>
            <Textarea
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              placeholder="Укажите причину удаления заказа..."
              rows={3}
              className="mt-1 mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOrder(null)}>Отмена</Button>
              <Button
                variant="destructive"
                disabled={!deleteReason.trim() || deleteLoading}
                onClick={handleDeleteOrder}
              >
                {deleteLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Удалить
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions — 2x2 grid of rectangular buttons */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-accent" />
            {t('manager.quickActions')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Button variant="outline" className="h-16 flex-col items-center justify-center gap-1.5 text-xs" onClick={() => navigate('/orders')} data-testid="quick-new-order">
              <Briefcase className="h-5 w-5 shrink-0" /> <span className="truncate w-full text-center">{t('manager.newOrder')}</span>
            </Button>
            <Button variant="outline" className="h-16 flex-col items-center justify-center gap-1.5 text-xs" onClick={() => navigate('/clients')} data-testid="quick-new-client">
              <AlertCircle className="h-5 w-5 shrink-0" /> <span className="truncate w-full text-center">{t('manager.clientsAction')}</span>
            </Button>
            <Button variant="outline" className="h-16 flex-col items-center justify-center gap-1.5 text-xs" onClick={() => navigate('/calendar')} data-testid="quick-calendar">
              <Calendar className="h-5 w-5 shrink-0" /> <span className="truncate w-full text-center">{t('manager.openCalendar')}</span>
            </Button>
            <Button variant="outline" className="h-16 flex-col items-center justify-center gap-1.5 text-xs" onClick={() => navigate('/team-load')} data-testid="quick-team-load">
              <Users className="h-5 w-5 shrink-0" /> <span className="truncate w-full text-center">Team Load</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Requires Attention — all alerts as square tiles in one card */}
      <Card className="cgc-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            {t('manager.needsAttention')}
          </CardTitle>
        </CardHeader>
        <CardContent className="pb-4">
          {(stats.unpaid_done > 0 || stats.remote_count > 0 || (serviceStats && serviceStats.total_services > 0) || stats.in_progress > 0) ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Unpaid Orders Tile */}
              {stats.unpaid_done > 0 && (
                <button
                  onClick={() => navigate('/orders?filter=needs_payment')}
                  className="p-4 rounded-xl border-2 border-red-500/30 bg-red-500/5 hover:bg-red-500/10 transition-all text-left group aspect-square flex flex-col justify-between"
                  data-testid="unpaid-orders-alert"
                >
                  <div>
                    <DollarSign className="h-5 w-5 text-red-500 mb-2" />
                    <p className="text-red-600 dark:text-red-400 font-bold text-sm leading-tight">
                      Получите оплату!
                    </p>
                  </div>
                  <div>
                    <p className="text-red-500 font-bold text-lg tabular-nums">
                      ${stats.unpaid_done_amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </p>
                    <p className="text-[10px] text-red-500/60 mt-0.5">
                      {stats.unpaid_done} {stats.unpaid_done === 1 ? 'заказ' : stats.unpaid_done < 5 ? 'заказа' : 'заказов'}
                    </p>
                  </div>
                </button>
              )}

              {/* Zone 50+ Tile */}
              {stats.remote_count > 0 && (
                <button
                  onClick={() => navigate('/orders?filter=remote_zone')}
                  className="p-4 rounded-xl border-2 border-orange-500/30 bg-orange-500/5 hover:bg-orange-500/10 transition-all text-left group aspect-square flex flex-col justify-between"
                  data-testid="remote-orders-alert"
                >
                  <div>
                    <Navigation className="h-5 w-5 text-orange-500 mb-2" />
                    <p className="text-orange-600 dark:text-orange-400 font-bold text-sm leading-tight">
                      Зона 50+
                    </p>
                  </div>
                  <div>
                    <p className="text-orange-500 font-bold text-lg tabular-nums">
                      {stats.remote_count}
                    </p>
                    <p className="text-[10px] text-orange-500/60 mt-0.5">
                      ${(stats.remote_amount || 0).toLocaleString()}
                    </p>
                  </div>
                </button>
              )}

              {/* Service Orders Tile */}
              {serviceStats && serviceStats.total_services > 0 && (
                <button
                  onClick={() => navigate('/orders?filter=service')}
                  className="p-4 rounded-xl border-2 border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 transition-all text-left group aspect-square flex flex-col justify-between"
                  data-testid="service-quality-widget"
                >
                  <div>
                    <Wrench className="h-5 w-5 text-amber-500 mb-2" />
                    <p className="text-amber-600 dark:text-amber-400 font-bold text-sm leading-tight">
                      Сервис
                    </p>
                  </div>
                  <div>
                    <p className="text-amber-500 font-bold text-lg tabular-nums">
                      {serviceStats.active_services}
                    </p>
                    <p className="text-[10px] text-amber-500/60 mt-0.5">
                      {serviceStats.worker_fault} раб. / {serviceStats.company_fault} комп.
                    </p>
                  </div>
                </button>
              )}

              {/* In Progress Tile */}
              {stats.in_progress > 0 && (
                <button
                  onClick={() => navigate('/orders?filter=in_progress')}
                  className="p-4 rounded-xl border-2 border-blue-500/30 bg-blue-500/5 hover:bg-blue-500/10 transition-all text-left group aspect-square flex flex-col justify-between"
                  data-testid="in-progress-alert"
                >
                  <div>
                    <Clock className="h-5 w-5 text-blue-500 mb-2" />
                    <p className="text-blue-600 dark:text-blue-400 font-bold text-sm leading-tight">
                      В работе
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-500 font-bold text-lg tabular-nums">
                      {stats.in_progress}
                    </p>
                    <p className="text-[10px] text-blue-500/60 mt-0.5">
                      заказов сейчас
                    </p>
                  </div>
                </button>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-green-500 opacity-50" />
              <p className="text-sm">{t('common.allClear')}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  if (user?.role === 'worker') return <WorkerCabinet user={user} />;
  return <ManagerDashboard user={user} />;
}
